import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { CpuChipIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { queryClient } from "../../queries/client";
import { useUpdateDevice, useGetAllLocations } from "../../queries/admin";
import DeviceImage from "./DeviceImage";
import { IDeviceModel } from "../../pages/user/CompanyPage";

export interface DeviceModel {
  deviceId: string;
  name: string;
  dataRate: number;
  imageKey: string;
  locationId: string;
}

const initialDeviceValues: DeviceModel = {
  deviceId: "",
  name: "",
  dataRate: 60,
  imageKey: "",
  locationId: "",
};

const dataRateOptions = [
  { value: 10, label: "Every 10 seconds" },
  { value: 20, label: "Every 20 seconds" },
  { value: 30, label: "Every 30 seconds" },
  { value: 45, label: "Every 45 seconds" },
  { value: 60, label: "Every 1 minute" },
  { value: 300, label: "Every 5 minutes" },
  { value: 900, label: "Every 15 minutes" },
  { value: 1800, label: "Every 30 minutes" },
  { value: 3600, label: "Every 1 hour" },
  { value: 21600, label: "Every 6 hours" },
  { value: 43200, label: "Every 12 hours" },
  { value: 86400, label: "Every 24 hours" },
];

interface EditDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: IDeviceModel | null;
}

export default function EditDeviceModal({ isOpen, onClose, device }: EditDeviceModalProps) {
  const [deviceModel, setDeviceModel] = useState<DeviceModel>(initialDeviceValues);
  const { mutate: updateDevice, isPending } = useUpdateDevice();
  const { data: locations, isLoading: locationsLoading } = useGetAllLocations();

  const handleSubmit = () => {
    if (!deviceModel.name.trim()) {
      toast("Device name is required", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    if (!deviceModel.locationId) {
      toast("Please select a location", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    if (!deviceModel.dataRate || deviceModel.dataRate <= 0) {
      toast("Please select a data rate", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    if (!device) {
      toast("No device selected", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    updateDevice(
      {
        deviceId: device.id,
        deviceData: {
          name: deviceModel.name,
          locationId: deviceModel.locationId,
          dataRate: deviceModel.dataRate,
          imageKey: deviceModel.imageKey,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["get-location-devices"] });
          setDeviceModel(initialDeviceValues);
          onClose();
        },
        onError: (error: any) => {
          toast(error?.response?.data?.errorMessage || "Failed to update device", {
            type: "error",
            autoClose: 2000,
          });
        },
      }
    );
  };

  // Effect to populate form when modal opens or device changes
  useEffect(() => {
    if (!isOpen) {
      setDeviceModel(initialDeviceValues);
    } else if (device && device.id) {
      const newDeviceModel = {
        deviceId: device.id,
        name: device.name || "",
        dataRate: device.dataRate || 60,
        imageKey: device.imageKey || "",
        locationId: "", // Will be set by the location effect
      };

      setDeviceModel(newDeviceModel);
    } else {
      console.warn("EditDeviceModal - Invalid device provided:", device);
    }
  }, [isOpen, device]);

  // Effect to update location ID when locations are loaded and deviceModel is ready
  useEffect(() => {
    if (
      isOpen &&
      device &&
      locations &&
      locations.length > 0 &&
      !locationsLoading &&
      deviceModel.deviceId === device.id &&
      deviceModel.name && // Ensure deviceModel is fully populated
      !deviceModel.locationId // Only set if not already set
    ) {
      // Add small delay to ensure select options are rendered
      setTimeout(() => {
        // Try multiple ways to match the location
        let matchingLocation = null;

        // First try: exact match by ID (if device.location is an ID)
        matchingLocation = locations.find(
          (location: any) => location.id?.toString() === device.location?.toString()
        );

        // Second try: exact match by name
        if (!matchingLocation) {
          matchingLocation = locations.find((location: any) => location.name === device.location);
        }

        // Third try: case-insensitive name match
        if (!matchingLocation && device.location) {
          const deviceLocationLower = device.location.trim().toLowerCase();
          matchingLocation = locations.find(
            (location: any) => location.name?.trim().toLowerCase() === deviceLocationLower
          );
        }

        if (matchingLocation) {
          setDeviceModel((prevModel) => ({
            ...prevModel,
            locationId: matchingLocation.id?.toString() || "",
          }));
        } else {
          const firstLocationId = locations?.[0]?.id?.toString() || "";
          if (firstLocationId) {
            setDeviceModel((prevModel) => ({
              ...prevModel,
              locationId: firstLocationId,
            }));
          } else {
            console.warn(
              "EditDeviceModal - No matching location found and no locations available."
            );
          }
        }
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [
    isOpen,
    device,
    locations,
    locationsLoading,
    deviceModel.deviceId,
    deviceModel.name,
    deviceModel.locationId,
  ]);

  return (
    <Transition show={isOpen}>
      <Dialog key={device?.id || "new"} className="relative z-50" onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-sm transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="space-y-8">
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-8 flex flex-row items-center justify-between">
                    <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
                      Edit Device
                    </h2>
                    <XMarkIcon
                      className="w-6 cursor-pointer h-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      onClick={onClose}
                    />
                  </div>

                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <div className="mt-2 grid grid-cols-1 gap-8 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                        >
                          Device Name *
                        </label>
                        <div className="mt-2">
                          <input
                            key={`name-${device?.id || "new"}-${deviceModel.name}`}
                            value={deviceModel.name}
                            id="name"
                            name="name"
                            type="text"
                            autoComplete="off"
                            placeholder="Enter device name"
                            onChange={(e) => {
                              setDeviceModel((prevModel) => ({
                                ...prevModel,
                                name: e.target.value,
                              }));
                            }}
                            className="block w-full px-3 rounded-lg border-0 py-2 text-gray-900 dark:text-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-6">
                        <label
                          htmlFor="location"
                          className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                        >
                          Location *
                        </label>
                        <div className="mt-2 relative">
                          <select
                            key={`location-${device?.id || "new"}-${deviceModel.locationId}`}
                            id="location"
                            name="location"
                            value={deviceModel.locationId}
                            onChange={(e) => {
                              setDeviceModel((prevModel) => ({
                                ...prevModel,
                                locationId: e.target.value,
                              }));
                            }}
                            className="block w-full h-10 pl-3 pr-10 py-1.5 border-0 rounded-lg appearance-none bg-white text-gray-900 dark:text-gray-200 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary text-sm sm:leading-6 cursor-pointer"
                          >
                            <option value="">Select a location</option>
                            {locationsLoading ? (
                              <option disabled>Loading locations...</option>
                            ) : (
                              <>
                                {locations?.map((location: any) => (
                                  <option key={location.id} value={location.id}>
                                    {location.name}
                                  </option>
                                ))}
                                {/* Show current device location if it doesn't exist in the locations list */}
                                {device?.location &&
                                  !locations?.find(
                                    (loc: any) =>
                                      loc.name === device.location || loc.id === device.location
                                  ) && (
                                    <option value={device.location} className="text-orange-600">
                                      {device.location} (Current - may be deleted)
                                    </option>
                                  )}
                              </>
                            )}
                          </select>
                          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                            <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div className="sm:col-span-6">
                        <label
                          htmlFor="dataRate"
                          className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                        >
                          Data Collection Rate *
                        </label>
                        <div className="mt-2 relative">
                          <select
                            key={`dataRate-${device?.id || "new"}-${deviceModel.dataRate}`}
                            id="dataRate"
                            name="dataRate"
                            value={deviceModel.dataRate}
                            onChange={(e) => {
                              setDeviceModel((prevModel) => ({
                                ...prevModel,
                                dataRate: parseInt(e.target.value),
                              }));
                            }}
                            className="block w-full h-10 pl-3 pr-10 py-1.5 border-0 rounded-lg appearance-none bg-white text-gray-900 dark:text-gray-200 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary text-sm sm:leading-6 cursor-pointer"
                          >
                            <option value="">Select data rate</option>
                            {dataRateOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                            <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div className="col-span-full">
                        <label
                          htmlFor="image"
                          className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                        >
                          Device Image
                        </label>
                        <div className="mt-2">
                          <DeviceImage deviceModel={deviceModel} setDeviceModel={setDeviceModel} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-end gap-8">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isPending}
                    className="text-sm cursor-pointer font-semibold leading-6 text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={
                      isPending ||
                      !deviceModel.name.trim() ||
                      !deviceModel.locationId ||
                      !deviceModel.dataRate ||
                      deviceModel.dataRate <= 0
                    }
                    className="rounded-lg cursor-pointer bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPending ? "Updating..." : "Update Device"}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
